import { DatePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { CasesFacade } from 'src/app/abstraction/cases.facade';
import { EvolutionsFacade } from 'src/app/abstraction/evolutions.facade';
import { AttentionStates } from 'src/app/core/enums/attentionsStates';
import { OrdersStates } from 'src/app/core/enums/ordersStates';
import { DeliveryNotePDFBody } from 'src/app/core/services/cases.service';
import { EvolutionsQPS } from 'src/app/core/services/evolutions.service';
import { FileUtilitiesService } from 'src/app/core/services/fileUtilities.service';
import { Attention } from 'src/app/shared/models/attention';
import { Evolution } from 'src/app/shared/models/evolution';
import { Order } from 'src/app/shared/models/order';
import { ProvisionFee } from 'src/app/shared/models/provisionFee';

export interface BlockedOrderItemRow {
    supplyName: string;
    quantity: number;
    provider: string;
}

export interface OrderDeliveryNoteContext {
    attention: Attention | null;
    deliveryNote: Evolution | null;
}

@Injectable({ providedIn: 'root' })
export class OrderSharedActionsService {
    ORDERS_STATES = OrdersStates;

    constructor(
        private casesFacade: CasesFacade,
        private evolutionsFacade: EvolutionsFacade,
        private fileUtilitiesService: FileUtilitiesService,
        private datePipe: DatePipe,
    ) { }

    getBlockedOrderItems(order: Order, attentions: Attention[]): BlockedOrderItemRow[] {
        const blockedProvisionFeeIds = new Set(
            (attentions ?? [])
                .filter((attention) =>
                    attention?.provisionFeeId != null &&
                    (attention?.state?.id === AttentionStates.INFORMADA || attention?.state?.id === AttentionStates.NO_INFORMADA)
                )
                .map((attention) => attention.provisionFeeId)
        );

        return (order?.items ?? [])
            .filter((item) => blockedProvisionFeeIds.has(item?.provisionFeeId))
            .map((item) => ({
                supplyName: item?.practice?.name,
                quantity: item?.quantityDelivered,
                provider: `${order?.provider?.surname ?? ''} ${order?.provider?.name ?? ''}`.trim(),
            }));
    }

    canModifyOrder(order: Order, attentions: Attention[]): boolean {
        return this.getBlockedOrderItems(order, attentions).length === 0;
    }

    async getDeliveryNoteContext(order: Order, attentions: Attention[]): Promise<OrderDeliveryNoteContext> {
        const attention = (attentions ?? []).find((item) => item?.agreementId === order?.agreement?.id) ?? null;
        if (!attention?.id) {
            return { attention: null, deliveryNote: null };
        }

        const qps: EvolutionsQPS = { attentionId: attention.id, fromProvidersWeb: true };
        const evolutions = await firstValueFrom(this.evolutionsFacade.loadEvolutionsByQPS(qps, false));
        const deliveryNotes = (evolutions ?? []).filter((evolution) => evolution?.hasMedia);

        return {
            attention,
            deliveryNote: deliveryNotes[deliveryNotes.length - 1] ?? null,
        };
    }

    downloadDeliveryNotePdf(order: Order, patientId: number): void {
        if (!patientId) return;

        const provisionFees = (order?.items ?? [])
            .map((item) => this.mapItemToProvisionFee(item))
            .filter((item): item is ProvisionFee => !!item);

        if (!provisionFees.length) return;

        const body: DeliveryNotePDFBody = {
            provisionFees,
            patientId,
        };

        this.casesFacade.generateOrderDeliveryNotePDF(body).subscribe((response) => {
            this.fileUtilitiesService.downloadFile(response);
        });
    }

    private mapItemToProvisionFee(item: any): ProvisionFee | null {
        const provisionFeeSource = item?.provisionFee
            ?? (Array.isArray(item?.provision?.provisionFees)
                ? item.provision.provisionFees.find((fee: any) => fee?.id === item?.provisionFeeId) ?? null
                : null);

        if (!provisionFeeSource) {
            return null;
        }

        const provisionFee: any = {
            ...provisionFeeSource,
            provisionId: item?.provision?.id,
            practice: item?.practice,
            arrangementId: provisionFeeSource?.arrangement?.id ?? provisionFeeSource?.arrangementId ?? null,
        };

        delete provisionFee.orders;
        delete provisionFee.agreement;
        delete provisionFee.invoiced;
        delete provisionFee.debited;
        delete provisionFee.delivered;
        delete provisionFee.total;
        delete provisionFee.totalAmount;
        delete provisionFee.totalDebited;
        delete provisionFee.totalDelivered;
        delete provisionFee.totalInvoiced;
        delete provisionFee.totalRealized;
        delete provisionFee.totalValue;
        delete provisionFee.userId;
        delete provisionFee.provider;
        delete provisionFee.authorization;
        delete provisionFee.caseModuleId;
        delete provisionFee.moduleId;
        delete provisionFee.items;
        delete provisionFee.role;
        delete provisionFee.arrangement;
        delete provisionFee.creationTimestamp;

        return provisionFee as ProvisionFee;
    }

    matTooltipText(order: Order): string {
        let string = '';
        !!order.orderDate
            ? (string +=
                'Fecha de entrega esperada: ' +
                this.datePipe.transform(order.orderDate, 'yyyy-MM-dd HH:mm') +
                '\n')
            : null;
        !!order.deliveryDate
            ? (string +=
                'Fecha de entrega realizada: ' +
                this.datePipe.transform(order.deliveryDate, 'yyyy-MM-dd HH:mm') +
                '\n')
            : null;
        return string;
    }

    setOrderColor(orderStateId: number): any {
        switch (orderStateId) {
            case this.ORDERS_STATES.CANCELADO:
                return { color: '#D02700', background_color: '#FFE9E9' }
            case this.ORDERS_STATES.EN_CURSO:
                return { color: '#EA750B', background_color: '#FFF4EB' }
            case this.ORDERS_STATES.CREADO:
                return { color: '#609E50', background_color: '#EAFEE5' }
            case this.ORDERS_STATES.ENTREGADO:
                return { color: '#093BBB', background_color: '#D6DEF4' }
            default:
                return { color: '#FFB800', background_color: '#FFF2DF' }
        }
    }
}
